import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import { apiErrorMessage } from '@/shared/api-client';
import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Label } from '@/shared/ui/components/label';
import {
  useAssignVenueWaiverTemplate,
  type ActiveWaiverTemplateAssignment,
  type WaiverTemplate,
} from './venues';

type VenueWaiverQrCodeProps = {
  value: string;
  ariaLabel: string;
};

export type VenueWaiverAssignmentTarget = {
  id: string;
  name: string;
  activeWaiverTemplate: Pick<ActiveWaiverTemplateAssignment, 'templateId' | 'version'> | null;
};

type AssignWaiverTemplateDialogProps = {
  venue: VenueWaiverAssignmentTarget | null;
  templates: WaiverTemplate[];
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
};

const venueWaiverQrCodeOptions = {
  errorCorrectionLevel: 'M',
  margin: 1,
  type: 'svg',
  width: 192,
} as const;

export function VenueWaiverQrCode({ value, ariaLabel }: VenueWaiverQrCodeProps) {
  const [markup, setMarkup] = useState<string>('');

  useEffect(() => {
    let isCurrent = true;

    void QRCode.toString(value, venueWaiverQrCodeOptions)
      .then((svgMarkup) => {
        if (isCurrent) {
          setMarkup(svgMarkup);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setMarkup('');
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [value]);

  if (!markup) {
    return null;
  }

  return (
    <div
      aria-label={ariaLabel}
      className="w-fit rounded-lg border bg-white p-3"
      role="img"
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

export function publicWaiverSigningUrl(venueId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(`/public/waivers/${venueId}/sign`, origin).toString();
}

export function AssignWaiverTemplateDialog({
  venue,
  templates,
  onOpenChange,
  onSaved,
}: AssignWaiverTemplateDialogProps) {
  const { t } = useTranslation('common');
  const assign = useAssignVenueWaiverTemplate();
  const [templateId, setTemplateId] = useState('');
  const [version, setVersion] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initialSelection = getInitialAssignmentSelection(venue, templates);
    if (!initialSelection) {
      setTemplateId('');
      setVersion('');
      setError(null);
      return;
    }
    setTemplateId(initialSelection.templateId);
    setVersion(initialSelection.version);
    setError(null);
  }, [venue, templates]);

  if (!venue) {
    return null;
  }
  const targetVenue = venue;

  const selectedTemplate = findWaiverTemplateById(templates, templateId);
  const versions = selectedTemplate?.versions ?? [];
  const previewVersion = Number(version);
  const previewAcknowledgementCount =
    selectedTemplate?.versions.find((item) => item.version === previewVersion)?.acknowledgements
      .length ?? 0;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!templateId || !version) {
      return;
    }

    setError(null);
    try {
      await assign.mutateAsync({
        venueId: targetVenue.id,
        input: { templateId, version: previewVersion },
      });
      onOpenChange(false);
      onSaved();
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues', caught));
    }
  }

  return (
    <Dialog open={venue !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>
              {t('venues.waivers.assign.title', { name: targetVenue.name })}
            </DialogTitle>
            <DialogDescription>{t('venues.waivers.assign.description')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="venue-waiver-template">{t('venues.waivers.fields.template')}</Label>
            <select
              id="venue-waiver-template"
              value={templateId}
              onChange={(event) => {
                const nextTemplateId = event.target.value;
                setTemplateId(nextTemplateId);
                const nextTemplate = findWaiverTemplateById(templates, nextTemplateId);
                setVersion(nextTemplate ? String(nextTemplate.latestVersion.version) : '');
              }}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="venue-waiver-version">{t('venues.waivers.fields.version')}</Label>
            <select
              id="venue-waiver-version"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
            >
              {versions.map((item) => (
                <option key={item.version} value={item.version}>
                  v{item.version}
                </option>
              ))}
            </select>
          </div>

          {selectedTemplate && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              {t('venues.waivers.assign.preview', {
                count: previewAcknowledgementCount,
              })}
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={assign.isPending || !templateId || !version}>
              {assign.isPending
                ? t('venues.waivers.assign.submitting')
                : t('venues.waivers.assign.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function getInitialAssignmentSelection(
  venue: VenueWaiverAssignmentTarget | null,
  templates: WaiverTemplate[],
) {
  if (!venue) {
    return null;
  }

  const firstTemplate = templates[0];
  if (!firstTemplate) {
    return null;
  }

  const templateId = venue.activeWaiverTemplate?.templateId ?? firstTemplate.id;
  const selectedTemplate = findWaiverTemplateById(templates, templateId) ?? firstTemplate;

  return {
    templateId,
    version: String(venue.activeWaiverTemplate?.version ?? selectedTemplate.latestVersion.version),
  };
}

function findWaiverTemplateById(templates: WaiverTemplate[], templateId: string) {
  return templates.find((template) => template.id === templateId) ?? null;
}
